import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Play } from "lucide-react";

const N = 4;

export function MemoryGrid({ onScore }) {
  const [phase, setPhase] = useState("idle"); // idle | show | input | wrong
  const [pattern, setPattern] = useState([]);
  const [lit, setLit] = useState(-1);
  const [inputIdx, setInputIdx] = useState(0);
  const [level, setLevel] = useState(0);
  const reported = useRef(false);

  const playPattern = async (pat) => {
    setPhase("show");
    await new Promise((r) => setTimeout(r, 500));
    for (const cell of pat) {
      setLit(cell);
      await new Promise((r) => setTimeout(r, 500));
      setLit(-1);
      await new Promise((r) => setTimeout(r, 200));
    }
    setPhase("input"); setInputIdx(0);
  };

  const nextLevel = (lvl) => {
    const pat = [];
    for (let i = 0; i < lvl + 2; i++) pat.push(Math.floor(Math.random() * N * N));
    setPattern(pat); setLevel(lvl); playPattern(pat);
  };

  const start = () => { reported.current = false; nextLevel(1); };

  const tap = (i) => {
    if (phase !== "input") return;
    setLit(i); setTimeout(() => setLit(-1), 150);
    if (pattern[inputIdx] === i) {
      const ni = inputIdx + 1;
      if (ni === pattern.length) setTimeout(() => nextLevel(level + 1), 400);
      else setInputIdx(ni);
    } else setPhase("wrong");
  };

  useEffect(() => { if (phase === "wrong" && !reported.current) { reported.current = true; onScore?.((level - 1) * 120); } }, [phase, level, onScore]);

  return (
    <div className="flex flex-col items-center" data-testid="memory-grid">
      <p className="font-mono text-neon-cyan text-lg mb-1" data-testid="grid-level">LEVEL: {level}</p>
      <p className="text-zinc-500 text-xs font-mono mb-4 h-4">{phase === "show" ? "Watch…" : phase === "input" ? "Repeat the pattern" : ""}</p>

      <div className="relative grid gap-2 p-2 bg-zinc-900 border-2 border-zinc-700 rounded-xl" style={{ gridTemplateColumns: `repeat(${N}, 1fr)`, width: "min(88vw, 320px)" }}>
        {Array.from({ length: N * N }).map((_, i) => (
          <button key={i} onClick={() => tap(i)} data-testid={`grid-cell-${i}`}
            className="aspect-square rounded-lg transition-all"
            style={{ background: lit === i ? "#00F0FF" : "#27272a", boxShadow: lit === i ? "0 0 16px #00F0FF" : "none", transform: lit === i ? "scale(0.95)" : "scale(1)" }} />
        ))}
        {(phase === "idle" || phase === "wrong") && (
          <div className="absolute inset-0 bg-black/85 rounded-xl flex flex-col items-center justify-center gap-3">
            {phase === "wrong" && <p className="font-display font-black text-xl text-neon-pink uppercase" data-testid="grid-wrong">Wrong! Level {level}</p>}
            <Button onClick={start} data-testid="grid-start" className="bg-neon-cyan text-black rounded-none font-bold uppercase">
              <Play className="w-4 h-4 mr-1 fill-black" /> {phase === "wrong" ? "Try Again" : "Start"}
            </Button>
          </div>
        )}
      </div>
      <p className="text-zinc-500 text-xs font-mono mt-3">Memorize the flashing pattern, then tap it back. It grows each level!</p>
    </div>
  );
}
